import { Component, HostListener, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ActivationStart, Router } from "@angular/router";
import { BroadcastService } from "@bbwt/modules/broadcasting";
import { RoutesService } from "@main/routes";
import { CurrentUserChangedData, PictureMode, UserService } from "@main/users";
import { FormButtonsConfig } from "@mipro/components";
import CryptoES from "crypto-es";
import { OverlayPanel } from "primeng/overlaypanel";
import { filter, Subject, takeUntil } from "rxjs";
import { PatientBasicInfo } from "./patient";
import { PatientService } from "./patient.service";
import { LinkUserService } from "@main/users";
import { UserHelperService } from "@main/users/user-helper.service";
import { Permissions } from "@main/permissions/permissions";
import { CoreRole } from "@main/roles";
import { AppOnlineStateService } from "@main/pwa/app-online-state.service";
import { PlayerOfflineStorage } from "@project/player/player-offline.storage";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { PatientSuggestion } from "@project/models/patient-suggestion";
import { VariableHubService } from "@main/reporting.v3/core/variables/variable-hub.service";
import { IVariableEmitter } from "@main/reporting.v3/core/variables/variable-emitter";
import { AppStorage } from "@bbwt/utils/app-storage";
import { v4 as uuidv4 } from "uuid";

@Component({
    selector: "patient-switch",
    templateUrl: "./patient-switch.component.html",
    styleUrls: ["./patient-switch.component.scss"]
})
export class PatientSwitchComponent implements OnInit, OnDestroy, IVariableEmitter {
    private _allPatients: PatientSuggestion[] = [];
    private _placeholderPatientId: string | null = null;
    private _isUpdatingPatientsList = false;

    // Variable handling settings.
    variableEmitterId: string = uuidv4();

    defaultAvatar = "assets/images/noavatar.png";
    patientAvatarUrl: string = this.defaultAvatar;
    loadingPatientAvatar: boolean = true;
    patients: PatientSuggestion[] = [];
    selectedPatient: PatientSuggestion;
    selectedPatientInfo: PatientBasicInfo;
    showDialog: boolean;
    buttonsConfig = <FormButtonsConfig>{
        saveLabel: "OK",
        saveFunc: () => this.ok(),
        cancelFunc: () => this.cancel()
    };
    placeholder = "Select " + this.namingConvService.endUserSingular;
    dialogHeader = "Please select a " + this.namingConvService.endUserSingular;

    isClinicalSafetyOfficer: boolean = false;
    isPatientSelectionLocked: boolean = false;

    private readonly destroyed$ = new Subject<void>();

    @ViewChild("basicinfo") basicInfo: OverlayPanel;
    @ViewChild("lockInfo") lockInfo: OverlayPanel;

    showAddPatientDialog: boolean;
    returnUrl: string = "/";

    constructor(
        private patientService: PatientService,
        private broadcastService: BroadcastService,
        private router: Router,
        private routesService: RoutesService,
        private appOnlineStateService: AppOnlineStateService,
        private userHelperService: UserHelperService,
        private namingConvService: NamingConventionService,
        private variableHubService: VariableHubService) {
        this.subscribeEvents();
    }

    async ngOnInit() {
        this.variableHubService.registerVariableEmitter(this);

        await this.updatePatientsList();

        const storedReturn = AppStorage.getItem<string>("attemptedUrl");
        if (storedReturn && !this.patientService.currentPatientId) {
            this.returnUrl = storedReturn;
            this.showDialog = true;
        }

        AppStorage.setItem("attemptedUrl", null);
        AppStorage.setItem("selectedPatient", null);

        this.broadcastService.broadcast(
            PatientService.CurrentPatientChangedEventName,
            this.patientService.currentPatientId
        );

        this.isClinicalSafetyOfficer = this.userHelperService.hasRole(CoreRole.ClinicalSafetyOfficer);
        if (this.isClinicalSafetyOfficer) {
            this.selectedPatient = null;
            this.patientService.currentPatientId = null;
        }
    }

    get offline() {
        return !this.appOnlineStateService.isAppOnline;
    }

    async onPlayerCreate(event) {
        if (this.offline) {
            await this.updatePatientsListOffline(event);
        } else {
            this._isUpdatingPatientsList = true;
            this.patientService.currentPatientId = event.patientId;
            await this.updatePatientsList(event.patientId);
            this.patientService.currentPatientId = event.patientId;
        }
    }

    ngOnDestroy(): void {
        this.variableHubService.unregisterVariableEmitter(this);

        this.destroyed$.next();
        this.destroyed$.complete();
    }

    private subscribeEvents() {

        this.broadcastService.broadcast(PatientService.CurrentPatientLockEventName, false);

        this.broadcastService
            .on<boolean>(PatientService.CurrentPatientLockEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(isPatientSelectionLocked  => {
                this.setPatientSelectionLock(isPatientSelectionLocked );
            });

        this.broadcastService
            .on(LinkUserService.UserSelectedOrganizationChangedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(async () => {
                const patientId = this.patientService.currentPatientId;
                this._isUpdatingPatientsList = true;
                try {
                    const data = await this.patientService.getAll();
                    this._allPatients = data.patients;
                    this.restoreLoggedPatient();

                    if (patientId && this.isPatientSelectionLocked) {
                        const found = this.patients.find(p => p.patientId === patientId || p.id === patientId);
                        if (!found) {
                            this.patientService.setPatientSelectionLock(false);
                        }
                    }

                    await this.updateSelectedPatient(patientId);
                } finally {
                    this._isUpdatingPatientsList = false;
                }
            });

        this.broadcastService
            .on<CurrentUserChangedData>(UserService.CurrentUserChangedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(data => {
                if (data.isLogout) {
                    this.selectedPatient = null;
                    this.patientService.currentPatientId = null;
                } else {
                    this.updatePatientsList(this.patientService.currentPatientId);
                }
            });

        this.broadcastService
            .on(UserHelperService.ClaimsUpdatedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(_ => {
                this.isClinicalSafetyOfficer = this.userHelperService.hasRole(CoreRole.ClinicalSafetyOfficer);
            });

        this.broadcastService
            .on(PatientService.RequiredPatientSelectionEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => {
                this.showDialog = true;
            });

        this.broadcastService
            .on<string>(PatientService.CurrentPatientChangedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(patientId => {
                if (!this._isUpdatingPatientsList) {
                    this.updateSelectedPatient(patientId);
                }
            });

        this.broadcastService
            .on<string>(PatientService.PatientsUpdatedEventName)
            .pipe(takeUntil(this.destroyed$))
            .subscribe(patientId => {
                this.updatePatientsList(patientId);
            });

        this.router.events
            .pipe(
                takeUntil(this.destroyed$),
                filter(e => e instanceof ActivationStart)
            )
            .subscribe((e: ActivationStart) => {
                const loggedPatientSelectable = e.snapshot?.data?.loggedPatientSelectable ?? true;
                if (!loggedPatientSelectable) {
                    this.excludeLoggedPatient();
                    if (this.patientService.currentPatientId === this.userHelperService.realPersonId) {
                        this.patientService.currentPatientId = null;
                    }
                } else {
                    this.restoreLoggedPatient();
                }
            });
    }

    private async updateSelectedPatient(patientId: string) {
        this.emitPatientId(patientId);

        if (!patientId) {
            this.selectedPatient = null;
            return;
        }

        const foundPatient = this.patients.find(p => p.patientId === patientId || p.id === patientId);

        if (foundPatient) {
            this.selectedPatient = foundPatient;
            this.patientService.updatePatientPlaceHolder({
                id: this.selectedPatient?.id,
                patientId: this.selectedPatient?.patientId,
                fullName: this.selectedPatient?.fullName
            });
            await this.getBasicInfo();
        } else if (this.isPatientSelectionLocked && this.patientService.rawPatientPlaceHolder?.fullName) {
            const placeholderFullName = this.patientService.rawPatientPlaceHolder.fullName;
            this.selectedPatient = {
                id: patientId,
                patientId: patientId,
                fullName: placeholderFullName,
                miProNumber: ""
            } as PatientSuggestion;
            this.addPlaceholderToOptions(this.selectedPatient);
        } else if (this._allPatients.length > 0) {
            this.patientService.currentPatientId = null;
        }
    }

    private async updatePatientsList(selectedPatientId?: string) {
        this._isUpdatingPatientsList = true;
        try {
            const data = await this.patientService.getAll();
            this._allPatients = data.patients;
            this.restoreLoggedPatient();
            await this.updateSelectedPatient(selectedPatientId);
        } finally {
            this._isUpdatingPatientsList = false;
        }
    }

    private async updatePatientsListOffline(event) {
        this._allPatients.push(event);
        if (event.id) {
            this.selectedPatient = event;
            await this.getBasicInfo();
        }
    }

    private emitPatientId(patientId: string) {
        // TODO: This is a patch, as emitted variables are not prepared
        //  to recognize ids and unhash them when they reach the backend.
        const unhashedPatientId = !!patientId?.includes("-")
            ? patientId.split("-")[1]
            : patientId;

        this.variableHubService.emitVariables(this.variableEmitterId, [{
            $type: "number",
            name: "patient_id",
            value: unhashedPatientId,
            empty: !unhashedPatientId,
            behaviorOnEmpty: "populate"
        }])
    }

    isReadOnly() {
        return this.isClinicalSafetyOfficer || this.isPatientSelectionLocked
    }
    showLockInfo(event: Event, target: any ){
        if (this.selectedPatient && this.isPatientSelectionLocked ){
            this.lockInfo.show(event, target )
        }
    }

    setPatientSelectionLock(isLocked: boolean) {
        this.isPatientSelectionLocked = isLocked;
        if (!isLocked) {
            this.removePlaceholderFromOptions();
        }
    }

    private addPlaceholderToOptions(placeholder: PatientSuggestion) {
        this.removePlaceholderFromOptions();
        const placeholderId = placeholder.patientId || placeholder.id;
        if (placeholderId && !this.patients.some(p => p.patientId === placeholderId || p.id === placeholderId)) {
            this._placeholderPatientId = placeholderId;
            this.patients = [placeholder, ...this.patients];
        }
        this.loadingPatientAvatar = false;
    }

    private removePlaceholderFromOptions() {
        if (this._placeholderPatientId) {
            this.patients = this.patients.filter(p =>
                p.patientId !== this._placeholderPatientId && p.id !== this._placeholderPatientId
            );
            this._placeholderPatientId = null;
        }
    }

    async onPatientSelect(patient: PatientSuggestion) {
        this.patientService.currentPatientPlaceHolder = {
            id: patient?.id,
            patientId: patient?.patientId,
            fullName: patient?.fullName
        };
        await this.getBasicInfo();
    }

    async getBasicInfo() {
        if (this.selectedPatient) {
            this.loadingPatientAvatar = false; // Temporary

            var patientInfoPlaceholder = {
                id: this.selectedPatient.patientId ?? this.selectedPatient.id,
                patientId: this.selectedPatient.patientId,
                fullName: this.selectedPatient.fullName,
                miProNumber: this.selectedPatient.miProNumber,
                externalId: this.selectedPatient.externalPatientId
            };

            if (PlayerOfflineStorage.get(patientInfoPlaceholder.id) != null || !this.appOnlineStateService.isAppOnline) {
                this.selectedPatientInfo = patientInfoPlaceholder;
            } else {
                await this.patientService.getPatientBasicInfo(this.selectedPatient.patientId ?? this.selectedPatient.id).then(data => {
                    this.selectedPatientInfo = { ...data, ...patientInfoPlaceholder };
                });
            }
        }
    }

    setPatientAvatar() {
        switch (this.selectedPatientInfo.pictureMode) {
            case PictureMode.Empty:
                this.patientAvatarUrl = this.defaultAvatar;
                break;
            case PictureMode.Gravatar:
                this.patientAvatarUrl =
                    "https://www.gravatar.com/avatar/" + CryptoES.MD5(this.selectedPatientInfo.gravatarEmail).toString();
                break;
            case PictureMode.Upload:
                this.patientAvatarUrl =
                    this.selectedPatientInfo.avatarImage != null ? this.selectedPatientInfo.avatarImage.url : this.defaultAvatar;
                break;
        }
        this.loadingPatientAvatar = false;
    }

    @HostListener("mouseout", ["$event.target"])
    onMouseOut(target) {
        if (target.id.length > 0) {
            this.basicInfo.hide();
        }
        this.lockInfo.hide();

        return false;
    }

    ok() {
        this.patientService.currentPatientId = this.selectedPatient.patientId || this.selectedPatient.id;
        this.showDialog = false;

        const storedReturn = AppStorage.getItem<string>("attemptedUrl");
        const returnUrl = storedReturn || this.returnUrl || "/";

        AppStorage.setItem("attemptedUrl", null);
        this.router.navigateByUrl(returnUrl);
    }

    cancel() {
        this.selectedPatient = null;
        this.patientService.currentPatientId = null;
        AppStorage.setItem("selectedPatient", null);
        this.showDialog = false;
        this.restoreLoggedPatient();
    }

    private excludeLoggedPatient() {
        this.patients = this._allPatients.filter(u => u.patientId !== this.userHelperService.realPersonId);
    }

    private restoreLoggedPatient() {
        this.patients = this._allPatients;
    }

    get canCreatePatient() {
        return this.userHelperService.hasPermission(Permissions.WritePatient);
    }
}
